import { DEFAULT_SPREAD } from '@ui/constants/uiConfig';
import {
  calculateSpreadPercentage,
  useSpreadPercentage,
} from '@ui/features/Spread/useSpreadPercentage';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { OrderType } from '@ui/types/trade';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { usePositionSize } from '../components/TradeModule/hooks/usePositionSize';
import { useIsLong } from '../components/TradeModule/store/useIsLong';
import { useOrderType } from '../components/TradeModule/store/useOrderType';
import { useTradingPair } from '../store/useTradingPair';
import { useFromInput } from './useFromInput';
/**
 * Use this hook for open position flow only
 *
 * @props isClosePrice
 * - true: for TP/SL, close price, use the reverse spread percentage for TP/SL
 * - false: for none close price relative
 *
 * check interface definition in src/features/Spread/useSpreadPercentage.ts -> calculateSpreadPercentage
 */
export const useOpenPositionPairSpreadPercentage = ({
  isClosePrice,
}: {
  isClosePrice: boolean;
}) => {
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());
  const isLong = useIsLong((s) => s._persistValue);
  const inputUsd = useFromInput((s) => s.input).value.usd; // fromUsd
  const positionSizeAfterFee = usePositionSize({
    inputUsd,
    // since this is open position, need to get the position size after open position's fees
    isBeforeFee: false,
  });
  const pairMarketPrice = usePriceLatest(selectedTradingPair.baseSymbol);
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;

  const triggerPrice = useFromInput((s) => s.input).triggerPrice; // fromUsd
  const triggerPriceNum =
    !isNil(triggerPrice) && !isNaN(Number(triggerPrice))
      ? BigNumber(triggerPrice)
      : undefined;

  const spreadPercentageInfo = useSpreadPercentage({
    tradePair: selectedTradingPair,
    positionSize: positionSizeAfterFee,
    pairPrice: isMarket ? pairMarketPrice : triggerPriceNum, // for market order use market price; for limit order use trigger price
  });

  return useMemo(() => {
    const slippageConfig = selectedTradingPair?.slippageConfig;

    return slippageConfig.enable
      ? calculateSpreadPercentage({
          isLong,
          spreadPercentageInfo,
          isClosePrice,
        })
      : DEFAULT_SPREAD; // for SC default is hardcoded to 0.01%
  }, [
    isClosePrice,
    isLong,
    selectedTradingPair?.slippageConfig,
    spreadPercentageInfo,
  ]);
};
